import { degToCompass } from "@/lib/conditions/scoring";

export type LocationPillData = {
  // Coastal
  spotType?: string | null;
  shelterLevel?: string | null;
  difficultyLevel?: string | null;
  tidePreference?: string | null;
  beachFacingDeg?: number | null;
  bottomType?: string | null;
  marineHighlights?: string | null;
  waterType?: string | null;
  prevailingWindDir?: string | null;
  typicalVisibility?: string | null;
  maxWaveSize?: string | null;
  bestWindDirection?: string | null;
  lifeguarded?: string | null;
  // Mountain (from resort_metrics + condition_locations)
  pisteKm?: number | null;
  verticalDropM?: number | null;
  topAltitudeM?: number | null;
  totalLifts?: number | null;
  glacier?: boolean;
  snowPark?: boolean;
  bikePark?: boolean;
  upliftAvailable?: boolean;
  beginnerFriendly?: boolean;
  mtbDifficulty?: string | null;
  terrainType?: string | null;
  treelineTerrain?: string | null;
};

type PillDef = {
  key: keyof LocationPillData;
  label: string;
  format?: "boolean" | "degrees" | "metres" | "km" | "treeline";
};

const SPORT_PILLS: Record<string, PillDef[]> = {
  surf: [
    { key: "spotType", label: "Type" },
    { key: "difficultyLevel", label: "Difficulty" },
    { key: "shelterLevel", label: "Shelter" },
    { key: "tidePreference", label: "Tide" },
    { key: "beachFacingDeg", label: "Facing", format: "degrees" },
    { key: "bottomType", label: "Bottom" },
    { key: "maxWaveSize", label: "Max size" },
  ],
  bodyboarding: [
    { key: "spotType", label: "Type" },
    { key: "difficultyLevel", label: "Difficulty" },
    { key: "shelterLevel", label: "Shelter" },
    { key: "tidePreference", label: "Tide" },
    { key: "beachFacingDeg", label: "Facing", format: "degrees" },
    { key: "bottomType", label: "Bottom" },
    { key: "maxWaveSize", label: "Max size" },
  ],
  wind: [
    { key: "prevailingWindDir", label: "Prevailing wind" },
    { key: "bestWindDirection", label: "Best wind" },
    { key: "difficultyLevel", label: "Difficulty" },
    { key: "shelterLevel", label: "Shelter" },
  ],
  foil: [
    { key: "spotType", label: "Type" },
    { key: "shelterLevel", label: "Shelter" },
    { key: "difficultyLevel", label: "Difficulty" },
  ],
  "downwind-foil": [
    { key: "spotType", label: "Type" },
    { key: "prevailingWindDir", label: "Prevailing wind" },
    { key: "shelterLevel", label: "Shelter" },
    { key: "difficultyLevel", label: "Difficulty" },
  ],
  "wing-foil": [
    { key: "prevailingWindDir", label: "Prevailing wind" },
    { key: "bestWindDirection", label: "Best wind" },
    { key: "difficultyLevel", label: "Difficulty" },
    { key: "shelterLevel", label: "Shelter" },
  ],
  diving: [
    { key: "shelterLevel", label: "Shelter" },
    { key: "difficultyLevel", label: "Difficulty" },
    { key: "waterType", label: "Water type" },
    { key: "typicalVisibility", label: "Visibility" },
    { key: "marineHighlights", label: "Marine life" },
  ],
  swim: [
    { key: "shelterLevel", label: "Shelter" },
    { key: "difficultyLevel", label: "Difficulty" },
    { key: "lifeguarded", label: "Lifeguarded" },
  ],
  sup: [
    { key: "shelterLevel", label: "Shelter" },
    { key: "difficultyLevel", label: "Difficulty" },
  ],
  "sup-touring": [
    { key: "shelterLevel", label: "Shelter" },
    { key: "difficultyLevel", label: "Difficulty" },
    { key: "waterType", label: "Water type" },
  ],
  "sup-surf": [
    { key: "spotType", label: "Type" },
    { key: "difficultyLevel", label: "Difficulty" },
    { key: "shelterLevel", label: "Shelter" },
    { key: "tidePreference", label: "Tide" },
    { key: "bottomType", label: "Bottom" },
  ],
  "sup-downwind": [
    { key: "prevailingWindDir", label: "Prevailing wind" },
    { key: "bestWindDirection", label: "Best wind" },
    { key: "shelterLevel", label: "Shelter" },
    { key: "difficultyLevel", label: "Difficulty" },
  ],
  sailing: [
    { key: "prevailingWindDir", label: "Prevailing wind" },
    { key: "shelterLevel", label: "Shelter" },
    { key: "difficultyLevel", label: "Difficulty" },
  ],
  piste: [
    { key: "pisteKm", label: "Piste", format: "km" },
    { key: "verticalDropM", label: "Vertical", format: "metres" },
    { key: "totalLifts", label: "Lifts" },
    { key: "glacier", label: "Glacier", format: "boolean" },
    { key: "snowPark", label: "Snow park", format: "boolean" },
    { key: "beginnerFriendly", label: "Beginner friendly", format: "boolean" },
  ],
  "off-piste": [
    { key: "verticalDropM", label: "Vertical", format: "metres" },
    { key: "topAltitudeM", label: "Summit", format: "metres" },
    { key: "glacier", label: "Glacier", format: "boolean" },
    { key: "treelineTerrain", label: "Tree skiing", format: "treeline" },
  ],
  touring: [
    { key: "verticalDropM", label: "Vertical", format: "metres" },
    { key: "topAltitudeM", label: "Summit", format: "metres" },
  ],
  mtb: [
    { key: "mtbDifficulty", label: "Difficulty" },
    { key: "terrainType", label: "Terrain" },
    { key: "bikePark", label: "Bike park", format: "boolean" },
    { key: "upliftAvailable", label: "Uplift", format: "boolean" },
  ],
};

function formatLabel(value: string): string {
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getPillsForSport(
  sport: string,
  data: LocationPillData
): { label: string; value: string }[] {
  const defs = SPORT_PILLS[sport];
  if (!defs) return [];

  const pills: { label: string; value: string }[] = [];

  for (const def of defs) {
    const raw = def.key === "bestWindDirection" && !data.bestWindDirection && data.beachFacingDeg != null
      ? degToCompass((data.beachFacingDeg + 180) % 360)
      : data[def.key];

    if (def.format === "boolean") {
      if (raw === true) pills.push({ label: def.label, value: "Yes" });
      continue;
    }

    if (raw == null || raw === "") continue;

    let formatted: string;
    switch (def.format) {
      case "degrees":
        formatted = degToCompass(raw as number);
        break;
      case "metres":
        formatted = `${(raw as number).toLocaleString("en-GB")} m`;
        break;
      case "km":
        formatted = `${raw} km`;
        break;
      case "treeline":
        formatted = raw === "extensive" ? "Extensive" : raw === "limited" ? "Limited" : "Above treeline";
        break;
      default:
        formatted = formatLabel(String(raw));
    }

    pills.push({ label: def.label, value: formatted });
  }

  return pills;
}
